
import { Ball, SimulationConfig, MorphData, RhythmicSimulationState } from '../types';
import { RhythmicLogic } from './modes/RhythmicLogic';
import { TrailLogic } from './modes/TrailLogic';
import { ShapeUtils } from './physics/ShapeUtils';
import { AUDIO } from '../services/AudioService';
import { PRESET_LIBRARY } from '../presets';

export class RhythmicSingleSimLogic {
  public static update(
    b: Ball,
    effectiveCfg: SimulationConfig,
    deltaTime: number,
    containerRadiusRef: any,
    ballGrowthRateRef: any,
    rhythmicTimeScaleRef: any,
    rhythmicTimeScaleRateRef: any,
    rotRad: number,
    hue: number,
    morphData: MorphData,
    collisionEventsRef: any,
    persistentSpectrumBoostRef: any,
    persistentSpectrumTargetRef: any,
    simTime: number,
    currentTS: number,
    shakeRef: any,
    triggerBlast: any,
    rhythmicBreakPiecesRef: any,
    rhythmicResetTimeoutRef: any,
    rhythmicIndexRef: any,
    initSimulation: any,
    setBounceCount: any,
    rhythmicSimulationsStateRef: any,
    setConfig: any,
    trailCircles: any[],
    trailCirclePool: any
  ) {
    if (effectiveCfg.rhythmicTimeScaleIncreaseMode === 'TIME') {
        rhythmicTimeScaleRef.current += rhythmicTimeScaleRateRef.current * (deltaTime / 1000);
        if (effectiveCfg.rhythmicTimeScaleAcceleration !== 0) {
            rhythmicTimeScaleRateRef.current += effectiveCfg.rhythmicTimeScaleAcceleration * (deltaTime / 1000);
        }
    }

    RhythmicLogic.update(
        b,
        effectiveCfg,
        deltaTime,
        0,
        0,
        containerRadiusRef,
        ballGrowthRateRef,
        rhythmicTimeScaleRef,
        rhythmicTimeScaleRateRef,
        rotRad,
        hue,
        morphData,
        collisionEventsRef,
        persistentSpectrumBoostRef,
        persistentSpectrumTargetRef,
        simTime,
        currentTS,
        (intensity) => { shakeRef.current = intensity; },
        triggerBlast,
        rhythmicBreakPiecesRef,
        rhythmicResetTimeoutRef,
        rhythmicIndexRef,
        initSimulation,
        () => setBounceCount((prev: number) => prev + 1),
        () => {
            AUDIO.resetEcho();
            rhythmicIndexRef.current = 0;
            if (rhythmicSimulationsStateRef.current) {
                rhythmicSimulationsStateRef.current.forEach((s: RhythmicSimulationState) => s.rhythmicIndex = 0);
            }
            setConfig((prev: SimulationConfig) => {
                if (!prev.playlistEnabled || prev.playlist.length === 0) return prev;
                const nextIndex = (prev.playlistCurrentIndex + 1) % prev.playlist.length;
                const nextItem = prev.playlist[nextIndex];
                let newConfig = { ...prev, playlistCurrentIndex: nextIndex, selectedMelody: nextItem.melodyId };
                if (nextItem.presetName && nextItem.presetName !== 'Default') {
                    const preset = PRESET_LIBRARY.find(p => p.name === nextItem.presetName);
                    if (preset) newConfig = { ...newConfig, ...preset.settings };
                }
                return newConfig;
            });
        }
    );

    if (effectiveCfg.trailLength > 0 || effectiveCfg.trailPersistence) {
        TrailLogic.updateTrail(b, effectiveCfg, deltaTime, 1, currentTS, trailCircles, hue, trailCirclePool);
    }

    // Boundary Clamp
    const relX = b.pos.x, relY = b.pos.y;
    const dist = Math.sqrt(relX * relX + relY * relY);
    const angle = Math.atan2(relY, relX);
    const baseR = containerRadiusRef.current;
    const effectiveShapeR = ShapeUtils.getInterpolatedRadiusAtAngle(angle - rotRad, morphData.from, morphData.to, morphData.factor, baseR, morphData.baseRadius) - (effectiveCfg.containerLineWidth || 4) / 2;
    if (dist + b.radius > effectiveShapeR) {
        const overlap = (dist + b.radius) - effectiveShapeR;
        const localPos = { x: relX * Math.cos(-rotRad) - relY * Math.sin(-rotRad), y: relX * Math.sin(-rotRad) + relY * Math.cos(-rotRad) };
        const localNormal = ShapeUtils.getNormal(localPos, morphData.from, baseR - (effectiveCfg.containerLineWidth || 4) / 2, morphData.to, morphData.factor, morphData.baseRadius);
        const worldNormal = { x: localNormal.x * Math.cos(rotRad) - localNormal.y * Math.sin(rotRad), y: localNormal.x * Math.sin(rotRad) + localNormal.y * Math.cos(rotRad) };
        b.pos.x -= worldNormal.x * (overlap + 0.5); b.pos.y -= worldNormal.y * (overlap + 0.5);
    }
  }
}
